import { EvaluatorToEvaluateeRole } from '../../commonTypes/evaluation/evaluator-impact.types';
import {
    EvaluationCalculate,
    CombinedEvaluations,
    GroupedScoresByCategory,
    GroupedScoresByEvaluator,
    AveragedScoresByCategory,
    AveragedScoresByEvaluator,
    WeightedScoresByEvaluator,
    CategoryBreakdown,
    DetailedScore,
    HolisticScore,
    EvaluationScoreLabel,
    EvaluationScoreValue,
} from '../../commonTypes/evaluation/calculate.types';
import { EvaluationRoleTopicWeight } from '../../commonTypes/user.types';
import { Topic } from '../../commonTypes/evaluation/evaluation.types';

export function calculateHolisticScore({
    combo,
    evaluatorRoleTopicWeights,
    technicalTopics,
    culturalTopics,
}: {
    combo: CombinedEvaluations;
    evaluatorRoleTopicWeights: EvaluationRoleTopicWeight[];
    technicalTopics: Topic[];
    culturalTopics: Topic[];
}): HolisticScore {
    if (combo.length === 0) {
        return {
            technicalScore: { finalScore: 0, categoryBreakdown: {} },
            culturalScore: { finalScore: 0, categoryBreakdown: {} },
        };
    }

    const technicalEvals: EvaluationCalculate[] = [];
    const culturalEvals: EvaluationCalculate[] = [];

    const technicalTopicIds = new Set(technicalTopics.map(t => t.id));
    const culturalTopicIds = new Set(culturalTopics.map(t => t.id));

    for (const evalItem of combo) {
        const techScores = evalItem.topicScores.filter(score => technicalTopicIds.has(score.topicId));
        const cultScores = evalItem.topicScores.filter(score => culturalTopicIds.has(score.topicId));

        if (techScores.length > 0) {
            technicalEvals.push({
                ...evalItem,
                topicScores: techScores,
            });
        }

        if (cultScores.length > 0) {
            culturalEvals.push({
                ...evalItem,
                topicScores: cultScores,
            });
        }
    }
    return {
        technicalScore: calculateScore(technicalEvals, evaluatorRoleTopicWeights),
        culturalScore: calculateScore(culturalEvals, evaluatorRoleTopicWeights),
    };
}

export function calculateScore(evaluations: EvaluationCalculate[], evaluatorRoleTopicWeights: EvaluationRoleTopicWeight[]): DetailedScore {
    if (evaluations.length === 0) {
        return { finalScore: 0, categoryBreakdown: {} };
    }
    const groupedScores = groupScoresByEvaluator(evaluations);
    const averagedScores = calculateAveragedScoresByCategory(groupedScores);
    const categoryBreakdown = calculateCategoryBreakdown(averagedScores, evaluatorRoleTopicWeights);
    const finalScore = calculateFinalScore(categoryBreakdown);

    return { finalScore, categoryBreakdown };
}

export function groupScoresByEvaluator(evaluations: EvaluationCalculate[]): GroupedScoresByCategory {
    const groupedScores: GroupedScoresByCategory = {};

    for (const evaluation of evaluations) {
        const { topicScores, evaluatorToEvaluateeRole } = evaluation;

        for (const score of topicScores) {
            const topicId = score.topicId;

            if (!groupedScores[topicId]) {
                groupedScores[topicId] = {};
            }

            if (!groupedScores[topicId]![evaluatorToEvaluateeRole.direction]) {
                groupedScores[topicId]![evaluatorToEvaluateeRole.direction] = [];
            }

            groupedScores[topicId]![evaluatorToEvaluateeRole.direction]!.push(score.score);
        }
    }

    return groupedScores;
}

export function calculateAveragedScoresByCategory(groupedScores: GroupedScoresByCategory): AveragedScoresByCategory {
    const averagedScoresByCategory: AveragedScoresByCategory = {};

    for (const [topicId, evaluatorScores] of Object.entries(groupedScores)) {
        averagedScoresByCategory[topicId] = calculateAveragedScores(evaluatorScores!);
    }

    return averagedScoresByCategory;
}

export function calculateAveragedScores(groupedScores: GroupedScoresByEvaluator): AveragedScoresByEvaluator {
    const averagedScores: AveragedScoresByEvaluator = {};

    for (const [evaluatorType, scores] of Object.entries(groupedScores)) {
        if (scores && scores.length > 0) {
            const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
            averagedScores[evaluatorType as EvaluatorToEvaluateeRole] = Number(avg.toFixed(10));
        }
    }

    return averagedScores;
}

export function calculateCategoryBreakdown(
    averagedScoresByCategory: AveragedScoresByCategory,
    evaluatorRoleTopicWeights: EvaluationRoleTopicWeight[]
): CategoryBreakdown {
    const categoryBreakdown: CategoryBreakdown = {};

    for (const [topicId, averagedEvaluatorScores] of Object.entries(averagedScoresByCategory)) {
        if (!averagedEvaluatorScores || Object.keys(averagedEvaluatorScores).length === 0) continue;

        const topicWeightEntry = evaluatorRoleTopicWeights.find(w => w.topicId === topicId);
        if (!topicWeightEntry) {
            continue;
        }
        const topicWeight = topicWeightEntry.weight;

        const weightedEvaluatorScores: WeightedScoresByEvaluator = {};
        let weightedSum = 0;
        let totalWeight = 0;

        for (const evaluatorType of Object.values(EvaluatorToEvaluateeRole)) {
            if (averagedEvaluatorScores[evaluatorType] !== undefined) {
                const evaluatorWeight = topicWeight;

                const score = averagedEvaluatorScores[evaluatorType]!;
                weightedEvaluatorScores[evaluatorType] = score * evaluatorWeight;

                weightedSum += weightedEvaluatorScores[evaluatorType]!;
                totalWeight += evaluatorWeight;
            }
        }

        if (totalWeight === 0) continue;

        categoryBreakdown[topicId] = {
            weightedEvaluatorScores,
            finalScore: weightedSum / totalWeight,
            categoryWeight: topicWeight,
        };
    }

    return categoryBreakdown;
}

export function calculateFinalScore(categoryBreakdown: CategoryBreakdown): number {
    let weightedSum = 0;
    let totalWeight = 0;

    for (const score of Object.values(categoryBreakdown)) {
        if (!score) continue;

        const final = Number(score.finalScore);
        const weight = Number(score.categoryWeight);

        if (isNaN(final) || isNaN(weight)) {
            console.warn('Invalid score or weight detected:', { final, weight });
            continue;
        }

        weightedSum += final * weight;
        totalWeight += weight;
    }

    if (totalWeight === 0) {
        return 0;
    }

    return weightedSum / totalWeight;
}

export function mapScore(score: EvaluationScoreLabel): number {
    switch (score) {
        case EvaluationScoreLabel.NEGATIVE:
            return 0.1;
        case EvaluationScoreLabel.NEUTRAL:
            return 0.2;
        case EvaluationScoreLabel.POSITIVE:
            return 0.3;
        default:
            return 0;
    }
}

/*
0.1 → -1
0.2 → 0
0.3 → 1
*/
export function normalizeScore(value: number): number {
    const normalized = (value - 0.2) / 0.1;
    return Math.max(-1, Math.min(1, normalized));
}

export function mapScoreLabelToValue(label: string): number {
    if (!label) return 0;
    switch (label) {
        case EvaluationScoreLabel.NEGATIVE:
            return EvaluationScoreValue.NEGATIVE;
        case EvaluationScoreLabel.NEUTRAL:
            return EvaluationScoreValue.NEUTRAL;
        case EvaluationScoreLabel.POSITIVE:
            return EvaluationScoreValue.POSITIVE;
        default:
            throw new Error(`Invalid score label: ${label}`);
    }
}
